// src/utils/stopUtils.js
import { getDistance } from 'geolib';

const serviceTypes = ['BUS', 'RAPIDKL'];

export function filterStopsByDistance(userLocation, stops, maxDistance) {
    if (!userLocation) return [];

    return stops.filter(stop => {
        const lat = parseFloat(stop.stop_lat);
        const lon = parseFloat(stop.stop_lon);
        const dist = getDistance(
            { latitude: userLocation.lat, longitude: userLocation.lng },
            { latitude: lat, longitude: lon }
        ) / 1000; // convert to km
        return dist <= maxDistance;
    });
}

export function formatDistance(distanceKm) {
    if (distanceKm === null || distanceKm === undefined || isNaN(distanceKm)) return "N/A";
    return distanceKm < 1
        ? `${(distanceKm * 1000).toFixed(0)} m`
        : `${distanceKm.toFixed(2)} km`;
}

// utils/calculateDistance.js
export function calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Radius of the Earth in km
    const toRad = (deg) => (deg * Math.PI) / 180;

    const phi1 = toRad(lat1);
    const phi2 = toRad(lat2);
    const deltaPhi = toRad(lat2 - lat1);
    const deltaLambda = toRad(lon2 - lon1);

    const a =
        Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
        Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // in km

    return distance;
}

export function getRouteIds(overlappingStops, stops, lat, lon) {
    const isSameCoord = (aLat, aLon, bLat, bLon) => {
        return parseFloat(aLat).toFixed(6) === parseFloat(bLat).toFixed(6) &&
            parseFloat(aLon).toFixed(6) === parseFloat(bLon).toFixed(6);
    };

    // Get overlapping stops at the given lat/lon
    let overlappingFilteredStops = [];
    if (overlappingStops && overlappingStops.length > 0) {
        overlappingFilteredStops = overlappingStops.flatMap(({ coord, stops }) => {
            const [olLat, olLon] = coord;
            return isSameCoord(lat, lon, olLat, olLon) ? stops : [];
        });
    }

    // Get normal stops at the same lat/lon
    let filteredStops = [];
    if (stops && stops.length > 0) {
        filteredStops = stops.filter(s =>
            isSameCoord(lat, lon, s.stop_lat, s.stop_lon)
        );
    }

    // Combine both
    const combinedStops = [...overlappingFilteredStops, ...filteredStops];
    // Extract unique route_ids
    const uniqueRouteIds = [...new Set(combinedStops.map(s => s.route_id))];

    return uniqueRouteIds;
}

export function getShapesByStopId(stop_id, stopTimes, trips, shapes) {

    const matchingShapeIds = new Set();

    // Step 1 & 2: Loop through all service types to find matching shape_ids
    for (const type of serviceTypes) {
        const tripIds = stopTimes[type]
            .filter(entry => entry.stop_id === stop_id)
            .map(entry => entry.trip_id);

        trips[type]
            .filter(trip => tripIds.includes(trip.trip_id))
            .forEach(trip => matchingShapeIds.add(trip.shape_id));
    }

    // Step 3: Gather shape points for all matching shape_ids
    const shapePointsGroupedByShapeId = [];

    for (const type of serviceTypes) {
        for (const shapeId of matchingShapeIds) {
            const points = shapes[type]
                .filter(shape => shape.shape_id === shapeId)
                .sort((a, b) => Number(a.shape_pt_sequence) - Number(b.shape_pt_sequence))
                .map(point => ({
                    lat: parseFloat(point.shape_pt_lat),
                    lon: parseFloat(point.shape_pt_lon),
                }));
            if (points.length > 0) {
                shapePointsGroupedByShapeId.push({
                    shape_id: shapeId,
                    points,
                });
            }
        }
    }

    return shapePointsGroupedByShapeId;
}


export function getStopsByStopId(stop_id, stops) {

    return serviceTypes.flatMap(type =>
        stops[type].filter(entry => entry.stop_id === stop_id)
    );
}

export function mapShapesToStops(trips, stopTimes, shapeIds) {

    const shapeStopMap = {};

    const shapeIdSet = new Set(shapeIds); // for faster lookup

    for (const service of serviceTypes) {
        // Filter only trips whose shape_id is in the list
        const relevantTrips = trips[service].filter(trip => shapeIdSet.has(trip.shape_id));

        relevantTrips.forEach(trip => {
            const { shape_id, trip_id } = trip;
            if (!shapeStopMap[shape_id]) shapeStopMap[shape_id] = new Set();
            stopTimes[service]
                .filter(st => st.trip_id === trip_id)
                .forEach(st => shapeStopMap[shape_id].add(st.stop_id));
        });
    }

    // Convert sets to arrays
    const result = {};
    for (const shapeId of shapeIds) {
        result[shapeId] = Array.from(shapeStopMap[shapeId] || []);
    }

    return result;
}

export function getStopsByIds(stops, stopIds) {
    const serviceTypes = ['BUS', 'LRT', 'MRT', 'BRT'];

    if (!stops || !stopIds) return [];
    const allStops = serviceTypes.flatMap(service => stops[service] || []);
    return allStops.filter(stop => stopIds.has(stop.stop_id));
}

export function getRelatedTripsByShapeIds(trips, shapeIds) {
    const shapeIdSet = new Set(shapeIds);

    return serviceTypes.flatMap(service =>
        (trips[service] || []).filter(trip => shapeIdSet.has(trip.shape_id))
    );
}

export function getRoutesByShapeIds(trips, routes, shapeIds) {
    const shapeIdSet = new Set(shapeIds);
    const routeIdSet = new Set();

    // Step 1: Find route_ids from matching trips
    for (const service of serviceTypes) {
        (trips[service] || []).forEach(trip => {
            if (shapeIdSet.has(trip.shape_id)) {
                routeIdSet.add(trip.route_id);
            }
        });
    }

    // Step 2: Collect matching route objects
    const relatedRoutes = serviceTypes.flatMap(service =>
        (routes[service] || []).filter(route => routeIdSet.has(route.route_id))
    );

    return relatedRoutes;
}

export function groupStopsByShapeId(shapeToStopsMap, allStops) {
    const stopMap = new Map(allStops.map(stop => [stop.stop_id, stop]));

    const result = {};

    for (const [shapeId, stopIds] of Object.entries(shapeToStopsMap)) {
        result[shapeId] = stopIds
            .map(id => stopMap.get(id))
            .filter(Boolean); // remove undefined if any stop_id not found
    }

    return result;
}

export function getRelatedRoutesByShapeIds(trips, routes, shapeIds) {
    // Step 1: Flatten all trips
    const allTrips = Object.values(trips)
        .flat()
        .filter(trip => trip && trip.shape_id && trip.route_id);

    // Step 2: Extract route_ids from trips with matching shape_ids
    const relatedRouteIds = new Set(
        allTrips
            .filter(trip => shapeIds.includes(trip.shape_id))
            .map(trip => trip.route_id)
    );

    // Step 3: Flatten all routes
    const allRoutes = Object.values(routes)
        .flat()
        .filter(route => route && route.route_id);

    // Step 4: Return routes that match related route_ids
    return allRoutes.filter(route => relatedRouteIds.has(route.route_id));
}

// Returns trips grouped by shape_id (flattened from all serviceTypes)
export function groupTripsByShapeId(tripsByServiceType) {
    const grouped = {};

    for (const serviceTrips of Object.values(tripsByServiceType)) {
        for (const trip of serviceTrips) {
            const shapeId = trip.shape_id;
            if (!grouped[shapeId]) {
                grouped[shapeId] = [];
            }
            grouped[shapeId].push(trip);
        }
    }

    return grouped;
}

export function groupStopsByShapeIdWithTrips(
    shapeToStopsMap,
    allStops,
    stopTimesByServiceType,
    tripsByServiceType
) {
    const stopMap = new Map(allStops.map(stop => [stop.stop_id, stop]));
    const flatResult = {}; // Not grouped by serviceType anymore

    for (const serviceType of serviceTypes) {
        const stopTimes = stopTimesByServiceType[serviceType] || [];
        const trips = tripsByServiceType[serviceType] || [];
        const tripMap = new Map(trips.map(trip => [trip.trip_id, trip]));

        // Build stop_id -> trip_ids map
        const stopToTripIdsMap = {};
        for (const st of stopTimes) {
            if (!st.stop_id || !st.trip_id) continue;
            if (!stopToTripIdsMap[st.stop_id]) {
                stopToTripIdsMap[st.stop_id] = new Set();
            }
            stopToTripIdsMap[st.stop_id].add(st.trip_id);
        }

        // Build grouped stops with one trip by shapeId
        for (const [shapeId, stopIds] of Object.entries(shapeToStopsMap)) {
            if (!flatResult[shapeId]) {
                flatResult[shapeId] = [];
            }

            const stops = stopIds
                .map(stopId => {
                    const stop = stopMap.get(stopId);
                    if (!stop) return null;

                    const tripIds = stopToTripIdsMap[stopId];
                    let associatedTrip = null;

                    if (tripIds) {
                        for (const tripId of tripIds) {
                            const trip = tripMap.get(tripId);
                            if (trip) {
                                associatedTrip = trip;
                                break;
                            }
                        }
                    }

                    return {
                        ...stop,
                        trip: associatedTrip,
                    };
                })
                .filter(Boolean); // remove nulls

            flatResult[shapeId].push(...stops);
        }
    }

    return flatResult;
}

export function getFrequenciesByTripId(frequencies, tripId) {
    const matchedFrequencies = [];

    for (const serviceType of serviceTypes) {
        const serviceFrequencies = frequencies[serviceType];

        for (const freq of serviceFrequencies) {
            if (freq.trip_id === tripId) {
                matchedFrequencies.push(freq);
            }
        }
    }

    return matchedFrequencies;
}



